import React from 'react';
import { Check } from 'lucide-react';

/* Selector de Inteligencia › Alertas: cuatro tarjetas grandes lado a lado (obra, cámara, entregas y clima).
   En celular quedan las cuatro en fila, compactas (ícono + nombre + estado), como un selector.
   Orden: 🏗️ Obra (la elegida al entrar), 📷 Cámara, 🚚 Entregas, 🌦️ Clima. No hace pedidos: recibe el resumen ya armado desde Alertas.jsx. */

export const TIPOS = [
  {
    key: 'obra',
    emoji: '🏗️',
    titulo: 'Alertas de la obra',
    corto: 'Obra',
    bajada: 'Obra parada, pocas horas, arranque tarde, menos gente o sin casco.',
    circulo: 'bg-amber-100',
    acento: 'border-t-amber-400',
  },
  {
    key: 'camara',
    emoji: '📷',
    titulo: 'Alertas de la cámara',
    corto: 'Cámara',
    bajada: 'Te avisamos si la cámara deja de mandar fotos.',
    circulo: 'bg-sky-100',
    acento: 'border-t-sky-400',
  },
  {
    key: 'entregas',
    emoji: '🚚',
    titulo: 'Alertas de entregas',
    corto: 'Entregas',
    bajada: 'Cuando llega un camión, un mixer o material, con foto. Incluido en IA de obra.',
    circulo: 'bg-emerald-100',
    acento: 'border-t-emerald-400',
  },
  {
    key: 'clima',
    emoji: '🌦️',
    titulo: 'Alertas de clima',
    corto: 'Clima',
    bajada: 'Lluvia mañana, lluvia fuerte, tormenta, viento para la grúa, calor o helada.',
    circulo: 'bg-indigo-100',
    acento: 'border-t-indigo-400',
  },
];

const ESTILO_ESTADO = {
  on: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  off: 'bg-gray-50 text-gray-500 border-gray-200',
  nuevo: 'bg-blue-50 text-blue-700 border-blue-200',
  alerta: 'bg-amber-50 text-amber-800 border-amber-200',
  cargando: 'bg-gray-50 text-gray-400 border-gray-200',
};
const PUNTO = { on: 'bg-emerald-500', off: 'bg-gray-300', nuevo: 'bg-blue-500', alerta: 'bg-amber-500', cargando: 'bg-gray-200' };

export function EstadoChip({ estado, className = '', compacto }) {
  if (!estado) return null;
  return (
    <span title={estado.txt} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${ESTILO_ESTADO[estado.tono] || ESTILO_ESTADO.off} ${className}`}>
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${PUNTO[estado.tono] || PUNTO.off}`} />
      {/* con cuatro tarjetas en el celular no entra el texto: queda solo el punto de color */}
      <span className={compacto ? 'hidden sm:inline' : ''}>{estado.txt}</span>
    </span>
  );
}

function Tarjeta({ tipo, estado, elegida, onElegir }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={elegida}
      aria-label={tipo.titulo}
      aria-controls={`alertas-panel-${tipo.key}`}
      id={`alertas-tab-${tipo.key}`}
      onClick={() => onElegir(tipo.key)}
      className={`relative h-full w-full text-left rounded-2xl border-2 transition-all p-2 sm:p-4 flex flex-col items-center sm:items-start gap-2 sm:gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
        elegida
          ? 'border-blue-600 bg-blue-50/60 shadow-md'
          : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
      }`}
    >
      {elegida && (
        <span className="hidden sm:flex absolute top-3 right-3 w-6 h-6 rounded-full bg-blue-600 text-white items-center justify-center">
          <Check size={14} strokeWidth={3} />
        </span>
      )}
      <span className={`w-11 h-11 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl sm:text-4xl leading-none shrink-0 ${tipo.circulo}`} aria-hidden="true">
        {tipo.emoji}
      </span>
      <span className="min-w-0 w-full flex flex-col items-center sm:items-start gap-1.5">
        <span className={`font-semibold text-gray-900 text-center sm:text-left leading-tight ${elegida ? 'text-blue-800' : ''}`}>
          <span className="sm:hidden text-sm">{tipo.corto}</span>
          <span className="hidden sm:inline text-base">{tipo.titulo}</span>
        </span>
        <span className="hidden sm:block text-xs text-gray-500 leading-snug">{tipo.bajada}</span>
        <span className="flex flex-col sm:flex-row sm:flex-wrap items-center gap-1 sm:gap-2 mt-auto sm:pt-1">
          <EstadoChip estado={estado} compacto className="max-sm:px-1.5" />
          {estado?.detalle && <span className="hidden sm:inline text-[11px] text-gray-500">{estado.detalle}</span>}
        </span>
      </span>
      {/* flechita que une la tarjeta elegida con su configuración */}
      {elegida && <span className="absolute -bottom-[9px] left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white border-r-2 border-b-2 border-blue-600 rounded-br-sm" aria-hidden="true" />}
    </button>
  );
}

const AlertasSelector = ({ elegida, onElegir, estados = {} }) => (
  <div>
    <div role="tablist" aria-label="Tipos de alertas" className="grid grid-cols-4 gap-1.5 sm:gap-3 items-stretch">
      {TIPOS.map((t) => (
        <Tarjeta key={t.key} tipo={t} estado={estados[t.key]} elegida={elegida === t.key} onElegir={onElegir} />
      ))}
    </div>
  </div>
);

export default AlertasSelector;
